// MBA Lab — team charter builder UI. Imports only lib/core.js and
// lib/charter-model.js (never layout.js - this bundle stays lean and
// standalone, matching roi.js's precedent). Event delegation over
// data-charter-key attributes means adding a field to the form never needs a
// new hand-wired listener - see handleFieldChange().
import { store, download, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import {
  SCHEMA_VERSION,
  LIMITS,
  TOTAL_SECTIONS,
  createDefaultState,
  sanitizeState,
  parseSavedState,
  serializeForSave,
  addMember,
  removeMember,
  renameMember,
  countCompletedSections,
  getDocumentModel,
  buildMarkdown,
} from './lib/charter-model.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'team-charter-builder';
const AUTOSAVE_DEBOUNCE_MS = 400;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

function getPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

/** Shallow-clones each object along the path for an immutable update. Never
 * used for the `members` array - see renameMember()'s doc comment. */
function setPath(state, path, value) {
  const keys = path.split('.');
  const next = { ...state };
  let cursor = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cursor[k] = { ...cursor[k] };
    cursor = cursor[k];
  }
  cursor[keys[keys.length - 1]] = value;
  return next;
}

class TeamCharterBuilder {
  constructor() {
    this.cacheElements();
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with
    // the feature off, this is a pure passthrough to the same localStorage wrapper.
    this.charterStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.rafId = null;
    this.saveTimer = null;
    this.hasEngaged = false;
    this.hasFiredResult = false;
    this.startOverConfirming = false;
    this.startOverTimer = null;
    this.restoreBar = null;
    this.dock = initToolDock({
      dock: byId('charter-dock'),
      jumpTarget: byId('charter-document'),
      returnTarget: byId('charter-form'),
    });

    this.state = this.loadInitialState();
    this.renderAll();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.form = byId('charter-form');
    this.els = {
      membersList: byId('charter-members-list'),
      newMemberName: byId('charter-new-member-name'),
      addMemberBtn: byId('charter-add-member-btn'),
      memberLimitNote: byId('charter-member-limit-note'),
      memberGoalsList: byId('charter-member-goals-list'),
      noMembersNote: byId('charter-no-members-note'),
      roleCoordinator: byId('charter-role-coordinator'),
      roleIntegrator: byId('charter-role-integrator'),
      roleQuality: byId('charter-role-quality'),
      roleLiaison: byId('charter-role-liaison'),
      startOver: byId('charter-start-over'),
      print: byId('charter-print'),
      download: byId('charter-download'),
      copy: byId('charter-copy'),
      actionFeedback: byId('charter-action-feedback'),

      docTitle: byId('charter-doc-title'),
      docSubtitle: byId('charter-doc-subtitle'),
      docMembers: byId('charter-doc-members'),
      docGoals: byId('charter-doc-goals'),
      docRoles: byId('charter-doc-roles'),
      docMeetings: byId('charter-doc-meetings'),
      docComms: byId('charter-doc-comms'),
      docQuality: byId('charter-doc-quality'),
      docDecisions: byId('charter-doc-decisions'),
      docAccountability: byId('charter-doc-accountability'),
      docStatement: byId('charter-doc-statement'),
      docRevisit: byId('charter-doc-revisit'),
      docSignatures: byId('charter-doc-signatures'),
    };
  }

  loadInitialState() {
    const saved = this.charterStore.load();
    if (!saved) return createDefaultState();

    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') {
      this.pendingRestoreNotice = { kind: 'restored', migrated: result.status === 'migrated' };
      return result.state;
    }
    if (result.status === 'unsupported-newer') {
      this.pendingRestoreNotice = {
        kind: 'unsupported-newer',
        raw: result.raw,
        message: "This charter was saved by a newer version of this tool - it hasn't been touched.",
      };
      return createDefaultState();
    }
    // 'malformed'
    this.pendingRestoreNotice = {
      kind: 'malformed',
      raw: result.raw,
      message: "Your saved charter couldn't be read - it hasn't been overwritten.",
    };
    return createDefaultState();
  }

  renderAll() {
    this.setFormValues();
    this.renderMembersList();
    this.renderRoleSelectOptions();
    this.renderMemberGoals();
    this.renderDocument();
    this.updateProgress();
    if (this.pendingRestoreNotice) {
      this.showRestoreNotice(this.pendingRestoreNotice);
      this.pendingRestoreNotice = null;
    }
  }

  setFormValues() {
    this.form.querySelectorAll('[data-charter-key]').forEach((el) => {
      const path = el.dataset.charterKey;
      const value = getPath(this.state, path);
      if (el.type === 'checkbox') {
        el.checked = value === true;
      } else if (el.type === 'radio') {
        el.checked = el.value === value;
      } else {
        el.value = value || '';
      }
    });
  }

  wireEvents() {
    this.form.addEventListener('input', (e) => this.handleFieldChange(e));
    this.form.addEventListener('change', (e) => this.handleFieldChange(e));

    this.els.addMemberBtn.addEventListener('click', () => this.handleAddMember());
    this.els.newMemberName.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleAddMember();
      }
    });

    this.els.startOver.addEventListener('click', () => this.handleStartOver());
    this.els.print.addEventListener('click', () => this.handlePrint());
    this.els.download.addEventListener('click', () => this.handleDownload());
    this.els.copy.addEventListener('click', () => this.handleCopy());
  }

  handleFieldChange(e) {
    const el = e.target.closest('[data-charter-key]');
    if (!el || !this.form.contains(el)) return;
    const path = el.dataset.charterKey;
    const value = el.type === 'checkbox' ? el.checked : el.value;
    this.state = sanitizeState(setPath(this.state, path, value));
    this.markEngaged();
    this.scheduleUpdate();
  }

  handleAddMember() {
    const name = this.els.newMemberName.value.trim();
    if (!name) return;
    if (this.state.members.length >= LIMITS.maxMembers) {
      this.els.memberLimitNote.hidden = false;
      return;
    }
    this.state = addMember(this.state, name);
    this.els.newMemberName.value = '';
    this.els.newMemberName.focus();
    this.els.memberLimitNote.hidden = this.state.members.length < LIMITS.maxMembers;
    this.renderMembersList();
    this.renderRoleSelectOptions();
    this.renderMemberGoals();
    this.markEngaged();
    this.scheduleUpdate();
  }

  handleRemoveMember(memberId) {
    this.state = removeMember(this.state, memberId);
    this.els.memberLimitNote.hidden = true;
    this.renderMembersList();
    this.renderRoleSelectOptions();
    this.renderMemberGoals();
    this.scheduleUpdate();
  }

  handleRenameMember(memberId, name) {
    this.state = renameMember(this.state, memberId, name);
    // Targeted updates only, not a full renderMembersList()/renderMemberGoals()
    // re-render - either would tear down and recreate the input the user is
    // actively typing into, stealing focus mid-keystroke.
    const updated = this.state.members.find((m) => m.id === memberId);
    this.updateMemberDerivedLabels(memberId, updated ? updated.name : '');
    this.renderRoleSelectOptions(); // labels may have changed
    this.scheduleUpdate();
  }

  /** The remove button's aria-label and the goal textarea's <label> both
   * embed the member's name at creation time - a rename must refresh both,
   * or they keep announcing the member's old name indefinitely. */
  updateMemberDerivedLabels(memberId, name) {
    const removeBtn = this.els.membersList.querySelector(`.tool-charter-member-remove[data-member-id="${memberId}"]`);
    if (removeBtn) removeBtn.setAttribute('aria-label', `Remove ${name || 'this member'}`);

    const goalLabel = this.els.memberGoalsList.querySelector(`label[data-member-id="${memberId}"]`);
    if (goalLabel) goalLabel.textContent = name || '(unnamed)';
  }

  renderMembersList() {
    clearChildren(this.els.membersList);
    for (const member of this.state.members) {
      const row = document.createElement('div');
      row.className = 'tool-charter-member-row';

      const nameInput = document.createElement('input');
      nameInput.type = 'text';
      nameInput.className = 'form-control';
      nameInput.maxLength = LIMITS.memberName;
      nameInput.value = member.name;
      nameInput.setAttribute('aria-label', 'Member name');
      nameInput.addEventListener('input', () => this.handleRenameMember(member.id, nameInput.value));

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'tool-charter-member-remove';
      removeBtn.dataset.memberId = member.id;
      removeBtn.setAttribute('aria-label', `Remove ${member.name || 'this member'}`);
      removeBtn.textContent = '✕';
      removeBtn.addEventListener('click', () => this.handleRemoveMember(member.id));

      row.append(nameInput, removeBtn);
      this.els.membersList.appendChild(row);
    }
  }

  renderRoleSelectOptions() {
    const roleSelects = [
      [this.els.roleCoordinator, 'coordinator'],
      [this.els.roleIntegrator, 'integrator'],
      [this.els.roleQuality, 'qualityBar'],
      [this.els.roleLiaison, 'liaison'],
    ];
    for (const [select, roleKey] of roleSelects) {
      const currentValue = this.state.roles[roleKey] || '';
      clearChildren(select);
      const unassigned = document.createElement('option');
      unassigned.value = '';
      unassigned.textContent = 'Unassigned';
      select.appendChild(unassigned);
      for (const member of this.state.members) {
        const opt = document.createElement('option');
        opt.value = member.id;
        opt.textContent = member.name || '(unnamed)';
        select.appendChild(opt);
      }
      select.value = currentValue;
    }
  }

  renderMemberGoals() {
    clearChildren(this.els.memberGoalsList);
    this.els.noMembersNote.hidden = this.state.members.length > 0;
    for (const member of this.state.members) {
      const wrap = document.createElement('div');
      wrap.className = 'tool-form-field';
      const textareaId = `charter-goal-${member.id}`;

      const label = document.createElement('label');
      label.setAttribute('for', textareaId);
      label.dataset.memberId = member.id;
      label.textContent = member.name || '(unnamed)';

      const textarea = document.createElement('textarea');
      textarea.className = 'form-control';
      textarea.id = textareaId;
      textarea.maxLength = LIMITS.learningGoal;
      textarea.rows = 2;
      textarea.dataset.charterKey = `goals.memberGoals.${member.id}`;
      textarea.value = this.state.goals.memberGoals[member.id] || '';

      wrap.append(label, textarea);
      this.els.memberGoalsList.appendChild(wrap);
    }
  }

  scheduleUpdate() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.renderDocument();
      this.updateProgress();
      this.scheduleAutosave();
      this.maybeTrackResult();
    });
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  maybeTrackResult() {
    if (this.hasFiredResult) {
      trackToolAction(TOOL_NAME, 'result', { sections_set: countCompletedSections(this.state) });
    }
    this.hasFiredResult = true;
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return; // over budget - keep whatever was previously saved, don't overwrite it
    this.charterStore.save(toSave);
  }

  updateProgress() {
    const count = countCompletedSections(this.state);
    this.dock?.setReading(`${count} of ${TOTAL_SECTIONS} sections set`);
    this.dock?.setComplete(count === TOTAL_SECTIONS);
  }

  renderDocument() {
    const doc = getDocumentModel(this.state);

    this.els.docTitle.textContent = doc.team.name || '[Team name]';
    const courseTerm = [doc.team.course, doc.team.term].filter(Boolean).join(' — ');
    this.els.docSubtitle.textContent = courseTerm || '[Course] — [Term]';

    this.renderDocList(this.els.docMembers, doc.members.length ? doc.members.map((m) => m.name || '[Unnamed member]') : ['[Add team members]']);

    const sectionEls = {
      goals: this.els.docGoals,
      roles: this.els.docRoles,
      meetings: this.els.docMeetings,
      comms: this.els.docComms,
      quality: this.els.docQuality,
      decisions: this.els.docDecisions,
      accountability: this.els.docAccountability,
    };
    for (const section of doc.sections) {
      const container = sectionEls[section.key];
      if (container) this.renderDocBlocks(container, section.blocks);
    }

    this.els.docStatement.textContent = doc.commitment.statement;
    if (doc.commitment.revisitDate) {
      this.els.docRevisit.textContent = `Revisit on: ${doc.commitment.revisitDate}`;
      this.els.docRevisit.hidden = false;
    } else {
      this.els.docRevisit.hidden = true;
    }

    clearChildren(this.els.docSignatures);
    if (doc.commitment.signatures.length) {
      const heading = document.createElement('p');
      heading.className = 'tool-charter-signatures-heading';
      heading.textContent = 'Signatures:';
      this.els.docSignatures.appendChild(heading);
      for (const name of doc.commitment.signatures) {
        const line = document.createElement('p');
        line.className = 'tool-charter-signature-line';
        const nameSpan = document.createElement('span');
        nameSpan.textContent = name;
        line.appendChild(nameSpan);
        this.els.docSignatures.appendChild(line);
      }
    }
  }

  renderDocList(el, items) {
    clearChildren(el);
    for (const item of items) {
      const li = document.createElement('li');
      li.textContent = item;
      el.appendChild(li);
    }
  }

  renderDocBlocks(el, blocks) {
    clearChildren(el);
    for (const block of blocks) {
      if (block.type === 'p') {
        const p = document.createElement('p');
        p.textContent = block.text;
        el.appendChild(p);
      } else if (block.type === 'list') {
        const ul = document.createElement('ul');
        for (const item of block.items) {
          const li = document.createElement('li');
          li.textContent = item;
          ul.appendChild(li);
        }
        el.appendChild(ul);
      }
    }
  }

  showRestoreNotice({ kind, migrated, raw, message }) {
    const bar = document.createElement('div');
    bar.className = 'tool-charter-restore-bar' + (kind === 'restored' ? '' : ' tool-charter-restore-bar--warn');
    bar.setAttribute('role', kind === 'restored' ? 'status' : 'alert');

    const text = document.createElement('span');
    text.textContent = kind === 'restored'
      // Not "from this browser" - since account sync, the restored copy may just as well have
      // hydrated down from another device, and the bar cannot tell which.
      ? (migrated ? 'Restored your saved charter (updated to the latest format).' : 'Restored your saved charter.')
      : message;
    bar.appendChild(text);

    if (raw) {
      const recoverBtn = document.createElement('button');
      recoverBtn.type = 'button';
      recoverBtn.className = 'tool-charter-restore-dismiss';
      recoverBtn.textContent = 'Download the old data';
      recoverBtn.addEventListener('click', () => download('team-charter-recovered.json', 'application/json', raw));
      bar.appendChild(recoverBtn);
    }

    const dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.className = 'tool-charter-restore-dismiss';
    dismissBtn.textContent = kind === 'restored' ? 'Dismiss' : 'Start fresh';
    dismissBtn.addEventListener('click', () => {
      if (kind === 'malformed') {
        // Genuinely unreadable by anyone - clearing it everywhere is the honest reset.
        this.charterStore.deleteAccountState();
      } else if (kind !== 'restored') {
        // unsupported-newer: only THIS build cannot read it - a stale cached bundle, or opening
        // the tool mid-rollback. The account copy is perfectly good to every current client, so
        // "start fresh" clears this device and leaves the account alone; tombstoning it here
        // would destroy data newer builds still read fine.
        this.charterStore.clear();
      }
      bar.remove();
      this.restoreBar = null;
    });
    bar.appendChild(dismissBtn);

    this.form.insertAdjacentElement('beforebegin', bar);
    this.restoreBar = bar;
  }

  handleStartOver() {
    if (!this.startOverConfirming) {
      this.startOverConfirming = true;
      this.els.startOver.textContent = 'Click again to confirm';
      this.startOverTimer = setTimeout(() => {
        this.startOverConfirming = false;
        this.els.startOver.textContent = 'Start over';
      }, 4000);
      return;
    }

    clearTimeout(this.startOverTimer);
    this.startOverConfirming = false;
    this.els.startOver.textContent = 'Start over';
    this.charterStore.deleteAccountState();
    this.state = createDefaultState();
    this.hasFiredResult = false;
    if (this.restoreBar) {
      this.restoreBar.remove();
      this.restoreBar = null;
    }
    this.renderAll();
  }

  handlePrint() {
    trackToolAction(TOOL_NAME, 'print');
    this.showActionFeedback('Opening print…');
    window.print();
  }

  handleDownload() {
    const markdown = buildMarkdown(this.state);
    const slug = (this.state.team.name || 'team-charter')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '') || 'team-charter';
    download(`${slug}.md`, 'text/markdown', markdown);
    this.showActionFeedback('Downloaded.');
    trackToolAction(TOOL_NAME, 'download');
  }

  async handleCopy() {
    const markdown = buildMarkdown(this.state);
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(markdown);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = this.fallbackCopy(markdown);

    this.showActionFeedback(copied ? 'Copied!' : 'Could not copy - try downloading instead.');
    if (copied) trackToolAction(TOOL_NAME, 'copy');
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  showActionFeedback(message) {
    this.els.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.els.actionFeedback.textContent === message) this.els.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('charter-form')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads
  // whatever won. A no-op (and no request) when signed out, and bounded by its own short
  // timeout so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new TeamCharterBuilder();
});
